import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, ArrowUpRight, MapPin, FileDown } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import { Calendar } from "../components/ui/calendar";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { SUMMIT, NETWORK, IMAGES, TITHELY_URL } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const summitDays = [
  new Date(2026, 9, 15),
  new Date(2026, 9, 16),
  new Date(2026, 9, 17),
  new Date(2026, 9, 18),
];

function RegistrationForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "Saint", church: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/registrations`, form);
      toast.success("Details saved. Continuing to Tithely to complete your registration.");
      setForm({ name: "", email: "", phone: "", role: "Saint", church: "" });
    } catch {
      toast.message("Continuing to Tithely to complete your registration.");
    } finally {
      setLoading(false);
      window.open(TITHELY_URL, "_blank", "noopener,noreferrer");
    }
  };

  const inputCls =
    "bg-black border-0 border-b border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-primary h-12 px-0 text-foreground font-body";

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="registration-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input data-testid="reg-name" placeholder="Full Name" value={form.name} onChange={set("name")} className={inputCls} />
        <Input data-testid="reg-email" type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
        <Input data-testid="reg-phone" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={inputCls} />
        <Input data-testid="reg-church" placeholder="Church / Ministry (optional)" value={form.church} onChange={set("church")} className={inputCls} />
      </div>
      <div>
        <label className="overline text-muted-foreground block mb-3">Your Office</label>
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger data-testid="reg-role" className="bg-black border-0 border-b border-white/20 rounded-none h-12 px-0 focus:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-muted border-border rounded-none">
            {["Saint", "Apostle", "Overseer", "Pastor", "Prophet", "Minister", "Student"].map((r) => (
              <SelectItem key={r} value={r} className="rounded-none">{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="reg-submit"
        className="group w-full bg-primary text-primary-foreground overline py-5 flex items-center justify-center gap-3 hover:bg-primary/85 transition-colors duration-300 disabled:opacity-60"
      >
        {loading ? "Ascending..." : "Register & Continue to Tithely"}
        <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
      </button>
      <p className="overline text-muted-foreground text-[0.6rem] text-center">
        Registration & payment completed securely on Tithely
      </p>
    </form>
  );
}

export default function Summit() {
  return (
    <div data-testid="summit-page">
      <PageHeader
        overline={`Annual Summit — ${SUMMIT.dates}`}
        lines={["PeakDwellers", "Summit '26"]}
        sub={`Theme: “${SUMMIT.theme}” — ${NETWORK.scriptureRef}`}
        image={IMAGES.peak}
      />

      {/* Theme + Calendar */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto py-20 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
        <Reveal className="md:col-span-7">
          <p className="overline text-primary mb-6">The Assignment</p>
          <p className="font-editorial text-3xl md:text-5xl text-foreground leading-tight">
            “{NETWORK.scripture}”
          </p>
          <p className="font-body text-muted-foreground mt-8 max-w-xl leading-relaxed">{SUMMIT.purpose}</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              href={SUMMIT.programBook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="program-book-download"
              className="group inline-flex items-center justify-center gap-3 bg-secondary text-background overline py-4 px-6 hover:bg-secondary/85 transition-colors"
            >
              <FileDown size={16} />
              Download the Program Book
              <ArrowUpRight size={14} className="opacity-70 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a
              href={SUMMIT.programBook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="program-book-view"
              className="inline-flex items-center justify-center gap-2 border border-border text-foreground overline py-4 px-6 hover:border-secondary hover:text-secondary transition-colors"
            >
              View on Phone
            </a>
          </div>
          <p className="font-body text-xs text-muted-foreground mt-3">PDF · 22 MB · Full schedule, speakers &amp; bios</p>
        </Reveal>
        <Reveal delay={0.15} className="md:col-span-5">
          <div className="border border-border bg-muted/30 p-6">
            <div className="flex items-center gap-3 mb-4">
              <CalendarDays size={18} className="text-secondary" />
              <span className="overline text-secondary">October 2026</span>
            </div>
            <Calendar
              mode="multiple"
              selected={summitDays}
              defaultMonth={new Date(2026, 9)}
              className="rounded-none [&_.rdp-day_selected]:bg-primary"
              data-testid="summit-calendar"
            />
            <p className="font-body text-sm text-muted-foreground mt-4">
              Four consecrated days of worship, impartation and apostolic alignment.
            </p>
            <div className="flex items-start gap-3 mt-5 pt-5 border-t border-border">
              <MapPin size={18} className="text-secondary shrink-0 mt-0.5" />
              <div>
                <p className="overline text-secondary text-[0.6rem] mb-1">Venue</p>
                <p className="font-body text-sm text-foreground" data-testid="summit-venue">{SUMMIT.venue}</p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Hosts + Official Flyer */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto py-20 grid grid-cols-1 md:grid-cols-12 gap-12 items-center" data-testid="summit-hosts">
        <Reveal className="md:col-span-6">
          <p className="overline text-primary mb-4">Presented by {SUMMIT.presenter}</p>
          <h2 className="font-display uppercase text-4xl md:text-6xl text-foreground mb-8">Your Hosts</h2>
          <div className="grid grid-cols-2 gap-px bg-border border border-border">
            {SUMMIT.hosts.map((h, i) => (
              <div key={i} className="bg-background overflow-hidden group" data-testid={`host-${i}`}>
                <div className="aspect-square overflow-hidden">
                  {h.image ? (
                    <img src={h.image} alt={h.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-muted/40">
                      <span className="font-display text-secondary/25 text-6xl">
                        {h.name.replace(/^(Dr\.|Apostle|Bishop|Overseer|Prophet|Pastor)\s+/i, "").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                      </span>
                      <span className="overline text-muted-foreground/60 text-[0.55rem] mt-3">Photo coming soon</span>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <p className="font-display uppercase text-sm text-foreground leading-tight">{h.name}</p>
                  <p className="overline text-secondary text-[0.55rem] mt-2">{h.role}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15} className="md:col-span-6">
          <div className="relative">
            <div className="absolute -inset-3 border border-secondary/40" />
            <img src={IMAGES.summitFlyer} alt="PeakDwellers Summit 2026 official flyer"
              className="relative w-full h-auto" data-testid="summit-flyer" />
          </div>
        </Reveal>
      </section>

      {/* Schedule */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto py-20" data-testid="summit-schedule">
        <Reveal className="mb-12">
          <p className="overline text-primary mb-4">The Program</p>
          <h2 className="font-display uppercase text-4xl md:text-7xl text-foreground">Four Days of Glory</h2>
        </Reveal>
        <Accordion type="single" collapsible defaultValue="d0" className="border-t border-border">
          {SUMMIT.schedule.map((day, i) => (
            <AccordionItem key={i} value={`d${i}`} className="border-b border-border">
              <AccordionTrigger data-testid={`day-trigger-${i}`} className="hover:no-underline py-8 group">
                <div className="flex items-baseline gap-6 text-left">
                  <span className="font-display text-secondary/50 text-3xl md:text-5xl group-hover:text-secondary transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <span className="font-display uppercase text-xl md:text-3xl text-foreground block">
                      {day.day} · {day.date}
                    </span>
                    <span className="overline text-muted-foreground text-[0.65rem]">{day.label}</span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="pl-0 md:pl-24 pb-6 space-y-px">
                  {day.sessions.map((s, j) => (
                    <div
                      key={j}
                      className="grid grid-cols-12 gap-4 py-4 border-t border-border/60 items-center"
                    >
                      <span className="col-span-3 md:col-span-2 overline text-secondary text-[0.65rem]">{s.time}</span>
                      <span className="col-span-9 md:col-span-6 font-body text-foreground">{s.title}</span>
                      <span className="col-span-12 md:col-span-4 font-editorial italic text-muted-foreground md:text-right">
                        {s.speaker}
                      </span>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Speakers */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto py-20" data-testid="summit-speakers">
        <Reveal className="mb-12">
          <p className="overline text-primary mb-4">The Voices</p>
          <h2 className="font-display uppercase text-4xl md:text-6xl text-foreground">Summit Speakers</h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border border border-border">
          {SUMMIT.speakers.map((sp, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.1 }}
              className="group relative bg-background overflow-hidden"
              data-testid={`speaker-${i}`}
            >
              <div className="aspect-square overflow-hidden">
                {sp.image ? (
                  <img
                    src={sp.image}
                    alt={sp.name}
                    className="w-full h-full object-cover scale-100 group-hover:scale-105 transition-transform duration-700"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-muted/40 relative">
                    <span className="font-display text-secondary/25 text-6xl md:text-7xl group-hover:text-secondary/50 transition-colors duration-500">
                      {sp.name.replace(/^(Dr\.|Apostle|Bishop|Overseer|Prophet|Pastor)\s+/i, "").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                    </span>
                    <span className="absolute bottom-4 overline text-muted-foreground/60 text-[0.55rem]">Photo coming soon</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/15 transition-colors duration-500" />
              </div>
              <div className="p-5">
                <p className="font-display uppercase text-sm md:text-base text-foreground leading-tight">{sp.name}</p>
                <p className="overline text-secondary text-[0.55rem] mt-2">{sp.title}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Registration */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto py-24 border-t border-border" data-testid="summit-register">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <p className="overline text-primary mb-6">Answer the Call</p>
            <h2 className="font-display uppercase text-5xl md:text-7xl text-foreground leading-[0.9]">
              Let Them<br />Come Up
            </h2>
            <p className="font-editorial italic text-2xl text-muted-foreground mt-8 max-w-md">
              Secure your place among the mighty men of valor. {SUMMIT.dates}.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-7">
            <RegistrationForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
